import { type ClientInquiry, ClientInquiryKind, textClientInquiryKind } from '@maru44/huntre-utils/src/models/clientInquiry'
import { Box, Button, Icon, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material'
import { useContext, useState } from 'react'
import { IoCheckmarkSharp } from 'react-icons/io5'
import { Link, useSearchParams } from 'react-router-dom'
import { Breads } from 'src/components/atoms/BreadCrumbs'
import { AuthContext } from 'src/components/providers/AuthProvider'
import { ClientContext } from 'src/components/providers/ClientProvider'
import { useClientInquiries } from 'src/hooks/clientInquiry/useClientInquiries'
import { formatTimestamp } from 'src/utils/firebase'
import { ClientInquiryFormDialog } from './ClientInquiryFormDialog'

export const ClientInquiriesPage = () => {
  const [user] = useContext(AuthContext)
  const { client } = useContext(ClientContext)

  const [searchParams] = useSearchParams()
  const inquiryKind = searchParams.get('inquiry') as ClientInquiryKind | null

  const { listClientInquiries, createInquiry } = useClientInquiries(client?.id ?? '')

  const [openCreate, setOpenCreate] = useState(!!inquiryKind)

  const handleChangePage = (_: unknown, newPage: number) => {
    listClientInquiries.goToPage(newPage)
  }

  return (
    <>
      <Box>
        <Breads breadIds={['inquiries']} />
        <Stack spacing={2}>
          <Typography variant="h5">お問合せ一覧</Typography>
          <ClientInquiriesTable
            data={listClientInquiries.data}
            totalCount={listClientInquiries.totalCount}
            page={listClientInquiries.page}
            pageSize={listClientInquiries.pageSize}
            onPageChange={handleChangePage}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
            }}
          >
            <Button onClick={() => setOpenCreate(true)} variant="contained">
              お問合せ
            </Button>
          </Box>
        </Stack>
      </Box>
      <ClientInquiryFormDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreate={createInquiry}
        mutate={listClientInquiries.mutate}
        userId={user?.uid ?? ''}
        defaultInquiryKInd={inquiryKind}
      />
    </>
  )
}

type ClientInquiriesTableProps = {
  data?: ClientInquiry[]
  totalCount: number
  page: number
  pageSize: number
  onPageChange: (event: unknown, newPage: number) => void
}

const ClientInquiriesTable = ({ data, totalCount, page, pageSize, onPageChange }: ClientInquiriesTableProps) => {
  return (
    <TableContainer component={Paper} elevation={6}>
      <Table sx={{ minWidth: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography variant="subtitle1">お問合せ内容</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1">お問合せ日時</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1">解決済</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.map((d) => (
            <ClientInquiryRow inquiry={d} key={d.id} />
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[pageSize]}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
      />
    </TableContainer>
  )
}

type RowProps = {
  inquiry: ClientInquiry
}

const ClientInquiryRow = ({ inquiry }: RowProps) => {
  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <Link to={`${inquiry.id}`}>
          <Typography>{inquiry.kind === ClientInquiryKind.Others ? inquiry.title : textClientInquiryKind(inquiry.kind)}</Typography>
        </Link>
      </TableCell>
      <TableCell>
        <Typography>{formatTimestamp(inquiry.createdAt)}</Typography>
      </TableCell>
      <TableCell>
        {!!inquiry.resolvedAt && (
          <Icon color="success">
            <IoCheckmarkSharp />
          </Icon>
        )}
      </TableCell>
    </TableRow>
  )
}
